import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Radio,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControlLabel,
  Switch,
  Card,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import WifiIcon from "@mui/icons-material/Wifi";
import NetworkCellIcon from "@mui/icons-material/NetworkCell";
import {
  useGetPhoneNUmberQuery,
  useGetTemplatesQuery,
} from "../store/services/whatsappApi";
import { jwtDecode } from "jwt-decode";
import {
  useCheckMpinStatusQuery,
  useVerifyMpinStatusMutation,
} from "../store/services/userApi";

// ─── Field styling (RCS-style) ────────────────────────────────────────────────
const fieldSx = {
  height: 35,
  borderRadius: "8px",
  background: "rgba(255,255,255,0.75)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#CBD5E1",
    borderWidth: "1px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#94A3B8",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#7c3aed",
    borderWidth: "2px",
  },
  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E2E8F0",
  },
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    ...fieldSx,
  },
};

const selectSx = {
  "& .MuiOutlinedInput-root": {
    ...fieldSx,
    "& .MuiSelect-select": {
      padding: "0 14px",
      fontSize: "0.8rem",
      display: "flex",
      alignItems: "center",
      height: "100%",
      boxSizing: "border-box",
      minHeight: "unset",
    },
    "& .MuiSelect-icon": {
      right: 8,
      fontSize: "1.15rem",
    },
  },
};

// ─── Phone preview ────────────────────────────────────────────────────────────
const PhonePreview = styled(Card)(() => ({
  width: "260px",
  height: "440px",
  margin: "0 auto",
  border: "7px solid #1a1a1a",
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  borderRadius: "20px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#f8f9fa",
}));

// ─── Step badge ───────────────────────────────────────────────────────────────
const StepBadge = ({ n }) => (
  <Box
    sx={{
      width: 22,
      height: 22,
      borderRadius: "50%",
      bgcolor: "#1976d2",
      color: "#fff",
      fontSize: "0.72rem",
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    {n}
  </Box>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getBodyText = (tpl) => {
  const comps = tpl?.components || [];
  const body = comps.find((c) => c.type === "BODY" && c.text);
  if (body) return body.text;
  const carousel = comps.find((c) => c.type === "CAROUSEL");
  const firstCard = carousel?.cards?.[0];
  const cardBody = firstCard?.components?.find(
    (c) => c.type === "BODY" && c.text,
  );
  return cardBody?.text || "";
};

const getHeaderImageUrl = (tpl) => {
  const comps = tpl?.components || [];
  const header = comps.find((c) => c.type === "HEADER" && c.example);
  const url = header?.example?.header_handle?.[0];
  if (url) return url;
  const carousel = comps.find((c) => c.type === "CAROUSEL");
  const firstCard = carousel?.cards?.[0];
  const cardHeader = firstCard?.components?.find(
    (c) => c.type === "HEADER" && c.example,
  );
  return cardHeader?.example?.header_handle?.[0] || "";
};

// ─── Template Variable helpers (WhatsApp uses {{1}}, {{2}}, …) ───────────────
const extractTemplateVariables = (text) => {
  if (!text) return [];
  const regex = /\{\{\s*(\d+)\s*\}\}/g;
  const set = new Set();
  let match;
  while ((match = regex.exec(text)) !== null) {
    set.add(match[1]);
  }
  return Array.from(set).sort((a, b) => Number(a) - Number(b));
};

const buildInitialVariableValues = (keys) => {
  const obj = {};
  keys.forEach((k) => {
    obj[k] = "";
  });
  return obj;
};

const substituteVariables = (text, variables) => {
  if (!text) return text;
  let result = text;
  Object.entries(variables || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      const pattern = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, "g");
      result = result.replace(pattern, value);
    }
  });
  return result;
};

// ─── Component ────────────────────────────────────────────────────────────────
const WhatsappConfig = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  let workflowMeta = {};
  try {
    const meta = localStorage.getItem(`workflow-meta-${id}`);
    if (meta) workflowMeta = JSON.parse(meta);
  } catch (e) {
    console.error("Failed to parse workflow meta", e);
  }
  const workflowName = workflowMeta?.name || "";

  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get("edit") === "true";
  const nodeId = searchParams.get("nodeId");

  const [customerId, setCustomerId] = useState(null);
  const [campaignName, setCampaignName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [waba_id, setWabaId] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [message, setMessage] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [filterDatatill_days, setFilterDatatill_days] = useState(0);
  const [templatePanelOpen, setTemplatePanelOpen] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [enableFilterTill, setEnableFilterTill] = React.useState(false);
  const [currentTime, setCurrentTime] = useState("11:50");
  const [templateVariables, setTemplateVariables] = useState({});

  useEffect(() => {
    const iv = setInterval(() => {
      const now = new Date();
      setCurrentTime(
        `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
      );
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (workflowName) setCampaignName(workflowName);
  }, [workflowName]);

  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        console.error("Access token not found");
        return;
      }

      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid || null;

      if (!id) {
        console.error("Customer ID not found in token", decoded);
      } else {
        setCustomerId(id);
      }
    } catch (err) {
      console.error("Invalid access token format", err);
    }
  }, []);

  useEffect(() => {
    if (isEditMode && customerId) {
      try {
        const stored = localStorage.getItem(`workflow-edit-graph-${id}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          const whatsappNode = parsed.nodes?.find((n) => n.id === nodeId);
          if (whatsappNode?.data?.whatsappConfig) {
            const c = whatsappNode.data.whatsappConfig;

            setCampaignName(c.campaignName || "");
            setPhoneNumber(c.phone_number_id || c.phoneNumber || "");
            setSelectedTemplateId(c.template_id || null);
            setTemplateName(c.templateName || "");
            setMessage(c.message || "");
            setMediaUrl(c.mediaUrl || "");
            setFilterDatatill_days(c.filterDatatill_days ?? 0);
            setWabaId(c.waba_id || "");

            if (c.template_variables && typeof c.template_variables === "object") {
              setTemplateVariables(c.template_variables);
            } else {
              const keys = extractTemplateVariables(c.message || "");
              setTemplateVariables(buildInitialVariableValues(keys));
            }
          }
        }
      } catch (err) {
        console.error("Failed to load WhatsApp config from edit graph", err);
      }
    }
  }, [isEditMode, id, customerId, nodeId]);

  console.log("waba_id in state", waba_id);
  const { data: phoneResp, isLoading: isPhoneLoading } = useGetPhoneNUmberQuery(
    customerId,
    { skip: !customerId },
  );
  const whatsappTemplateQueryArg = useMemo(() => {
    return customerId ? { customer_id: customerId } : undefined;
  }, [customerId]);

  const { data: templatesResp, isLoading: isTplLoading } = useGetTemplatesQuery(
    whatsappTemplateQueryArg,
    { skip: !customerId },
  );
  const { data: checkmpin } = useCheckMpinStatusQuery();
  const [verifyMpin] = useVerifyMpinStatusMutation();

  const phoneNumbers = phoneResp?.phone_numbers || [];
  const templates = templatesResp?.templates || [];

  useEffect(() => {
    if (!phoneNumber && phoneNumbers.length > 0)
      setPhoneNumber(phoneNumbers[0].phone_number_id);
  }, [phoneNumbers, phoneNumber]);

  const filteredTemplates = useMemo(() => {
    const q = templateSearch.toLowerCase();
    return templates
      .filter((t) => t.status === "APPROVED")
      .filter((t) => {
        const body = getBodyText(t).toLowerCase();
        return t.name.toLowerCase().includes(q) || body.includes(q);
      });
  }, [templates, templateSearch]);

  const sampleLength = message.length;

  const handleVariableChange = (key, value) => {
    setTemplateVariables((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveToWorkflow = () => {
    try {
      const key = isEditMode
        ? `workflow-edit-graph-${id}`
        : `workflow-graph-${id}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        localStorage.setItem(
          key,
          JSON.stringify({
            nodes: parsed.nodes.map((n) =>
              n.id === nodeId
                ? {
                    ...n,
                    data: {
                      ...n.data,
                      templateName,
                      whatsappConfig: {
                        campaignName,
                        phone_number_id: phoneNumber,
                        template_id: selectedTemplateId,
                        templateName,
                        message,
                        mediaUrl,
                        filterDatatill_days,
                        waba_id: waba_id,
                        template_variables: templateVariables,
                      },
                    },
                  }
                : n,
            ),
            edges: parsed.edges || [],
          }),
        );
      }
    } catch (err) {
      console.error("Failed to update whatsapp workflow graph", err);
    }
    navigate(isEditMode ? `/workflow/edit/${id}` : `/workflow/${id}`);
  };

  const handleTemplateRowClick = (tpl) => {
    setSelectedTemplateId(tpl.template_id);
    setTemplateName(tpl.name);
    const bodyText = getBodyText(tpl);
    setMessage(bodyText);
    setMediaUrl(getHeaderImageUrl(tpl));
    setWabaId(tpl.waba_id);
    setTemplateVariables(
      buildInitialVariableValues(extractTemplateVariables(bodyText)),
    );
    setTemplatePanelOpen(false);
  };

  const variableKeys = Object.keys(templateVariables);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        height: "100vh",
        bgcolor: "#f0f2f8",
        display: "flex",
        flexDirection: "column",
        fontSize: "0.8rem",
      }}
    >
      {/* ── Top bar ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          bgcolor: "rgba(255,255,255,0.8)",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          flexShrink: 0,
        }}
      >
        <IconButton
          size="small"
          onClick={() =>
            navigate(isEditMode ? `/workflow/edit/${id}` : `/workflow/${id}`)
          }
        >
          <ArrowBackIosNewIcon sx={{ fontSize: 15, color: "black" }} />
        </IconButton>
        <WhatsAppIcon sx={{ fontSize: 18, color: "#21b35b" }} />
        <Typography sx={{ fontSize: "0.88rem", fontWeight: 600 }}>
          WhatsApp Campaign{" "}
          {isEditMode && (
            <span style={{ fontWeight: 400, color: "#888" }}>(Edit)</span>
          )}
        </Typography>
      </Box>

      {/* ── Body ── */}
      <Box
        sx={{
          flex: 1,
          p: 2,
          display: "flex",
          gap: 2,
          overflow: "hidden",
          alignItems: "flex-start",
        }}
      >
        {/* LEFT: form */}
        <Paper
          elevation={0}
          sx={{
            flex: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.5)",
            backdropFilter: "blur(8px)",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            height: "100%",
            overflowY: "auto",
          }}
        >
          {/* Step 1 */}
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.2 }}
            >
              <StepBadge n={1} />
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                Create Campaign
              </Typography>
            </Box>

            {/* Campaign Name + Phone Number on the same row, both fixed width */}
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                label="Campaign Name"
                value={campaignName}
                InputProps={{ readOnly: true }}
                sx={{ width: 240, ...textFieldSx }}
              />
              <FormControl size="small" sx={{ width: 240, ...selectSx }}>
                <InputLabel>Phone Number</InputLabel>
                <Select
                  value={phoneNumber}
                  label="Phone Number"
                  onChange={(e) => setPhoneNumber(e.target.value)}
                >
                  {isPhoneLoading && <MenuItem disabled>Loading…</MenuItem>}
                  {!isPhoneLoading && phoneNumbers.length === 0 && (
                    <MenuItem disabled>No numbers found</MenuItem>
                  )}
                  {phoneNumbers.map((p) => (
                    <MenuItem key={p.id} value={p.phone_number_id}>
                      {p.phone_number}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Filter From LC */}
          {checkmpin?.data?.allow7_day_filter && (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={enableFilterTill}
                    onChange={(e) => {
                      setEnableFilterTill(e.target.checked);
                      if (!e.target.checked) setFilterDatatill_days(0);
                    }}
                  />
                }
                label="Filter From LC"
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "0.75rem" },
                }}
              />
              {enableFilterTill && (
                <FormControl
                  size="small"
                  sx={{ mt: 0.8, width: 150, ...selectSx }}
                >
                  <InputLabel>Filter Till</InputLabel>
                  <Select
                    value={filterDatatill_days}
                    label="Filter Till"
                    onChange={(e) => setFilterDatatill_days(e.target.value)}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 224 } } }}
                  >
                    <MenuItem value={1}>Today</MenuItem>
                    <MenuItem value={2}>Yesterday</MenuItem>
                    {Array.from({ length: 28 }, (_, i) => (
                      <MenuItem key={i + 3} value={i + 3}>
                        Last {i + 3} days
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}

          <Divider sx={{ borderColor: "rgba(0,0,0,0.07)" }} />

          {/* Step 2 */}
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.2 }}
            >
              <StepBadge n={2} />
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                Select Template
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1,
                mb: 1,
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                placeholder="Template Name"
                value={templateName}
                InputProps={{ readOnly: true }}
                sx={{ width: 240, ...textFieldSx }}
              />
              <Button
                variant="outlined"
                onClick={() => setTemplatePanelOpen(true)}
                sx={{
                  textTransform: "none",
                  fontSize: "0.73rem",
                  px: 1.5,
                  py: 0,
                  minWidth: 0,
                  whiteSpace: "nowrap",
                  height: 35,
                  flexShrink: 0,
                }}
              >
                Choose
              </Button>
            </Box>

            <Typography
              sx={{ fontSize: "0.67rem", color: "text.secondary", mb: 0.6 }}
            >
              Use approved WhatsApp templates with variables and optional media.
            </Typography>
          </Box>

          {/* Template Variables — narrower fields, 3 per row */}
          {variableKeys.length > 0 && (
            <Box>
              <Divider sx={{ borderColor: "rgba(0,0,0,0.07)", mb: 1.5 }} />
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <StepBadge n={3} />
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                  Template Variables
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "0.67rem",
                  color: "text.secondary",
                  mb: 1,
                }}
              >
                Fill values for the {"{{N}}"} placeholders in the message body.
              </Typography>

              <Grid container spacing={1.2}>
                {variableKeys.map((key) => (
                  <Grid item xs={4} key={key}>
                    <TextField
                      label={`{{${key}}}`}
                      value={templateVariables[key] ?? ""}
                      onChange={(e) =>
                        handleVariableChange(key, e.target.value)
                      }
                      size="small"
                      fullWidth
                      sx={textFieldSx}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Paper>

        {/* MIDDLE: phone preview */}
        <Paper
          elevation={0}
          sx={{
            flex: 1.2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.5)",
            backdropFilter: "blur(8px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            flexShrink: 0,
          }}
        >
          <PhonePreview>
            <Box
              sx={{
                height: 26,
                px: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: "#000",
                color: "#fff",
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: "9px" }}>{currentTime}</Typography>
              <Box sx={{ display: "flex", gap: 0.4, alignItems: "center" }}>
                <WifiIcon sx={{ fontSize: 9 }} />
                <NetworkCellIcon sx={{ fontSize: 9 }} />
                <BatteryFullIcon sx={{ fontSize: 9 }} />
              </Box>
            </Box>

            <Box
              sx={{
                height: 44,
                px: 1.5,
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid #e0e0e0",
                bgcolor: "#fff",
                flexShrink: 0,
              }}
            >
              <IconButton size="small" sx={{ p: 0.4, mr: 0.8 }}>
                <ArrowBackIosNewIcon sx={{ fontSize: 15 }} />
              </IconButton>
              <Typography sx={{ fontSize: "11px", fontWeight: 600 }}>
                Business Message
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
                overflowY: "auto",
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  bgcolor: "#f7f7f7",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 1.5,
                }}
              >
                {mediaUrl ? (
                  <Box
                    component="img"
                    src={mediaUrl}
                    alt="Template media"
                    sx={{
                      width: 160,
                      height: 160,
                      objectFit: "cover",
                      borderRadius: 1,
                    }}
                  />
                ) : (
                  <Typography
                    sx={{
                      fontSize: "0.68rem",
                      color: "text.disabled",
                      textAlign: "center",
                      color: "black",
                    }}
                  >
                    Template image will appear here
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  width: "100%",
                  bgcolor: "#fff",
                  borderRadius: 2,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                  p: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: message ? "text.primary" : "text.disabled",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    lineHeight: 1.4,
                  }}
                >
                  {substituteVariables(message, templateVariables) ||
                    "Thank you for showing interest in Fab Mediatech. Our executive will get in touch with you shortly."}
                </Typography>
              </Box>

              <Box sx={{ mt: "auto", width: "100%" }}>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    textTransform: "uppercase",
                    fontSize: "0.72rem",
                    borderRadius: 1.5,
                    py: 0.6,
                    bgcolor: "#007bff",
                    "&:hover": { bgcolor: "#0069d9" },
                  }}
                >
                  Connect
                </Button>
              </Box>
            </Box>
          </PhonePreview>
        </Paper>

        {/* RIGHT: template panel */}
        {templatePanelOpen && (
          <Paper
            elevation={0}
            sx={{
              width: 300,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.5)",
              backdropFilter: "blur(8px)",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                px: 1.8,
                py: 1,
                borderBottom: "1px solid rgba(0,0,0,0.08)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                WhatsApp Templates
              </Typography>
              <IconButton
                size="small"
                onClick={() => setTemplatePanelOpen(false)}
              >
                <CloseIcon sx={{ fontSize: 15, color: "red" }} />
              </IconButton>
            </Box>

            <Box sx={{ px: 1.5, py: 0.8, flexShrink: 0 }}>
              <TextField
                size="small"
                placeholder="Search templates…"
                fullWidth
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                InputProps={{
                  sx: {
                    fontSize: "0.75rem",
                    height: 32,
                    bgcolor: "rgba(255,255,255,0.6)",
                  },
                }}
              />
            </Box>

            <Divider sx={{ borderColor: "rgba(0,0,0,0.07)" }} />

            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {isTplLoading ? (
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    textAlign: "center",
                    py: 2,
                  }}
                >
                  Loading templates…
                </Typography>
              ) : (
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontSize: "0.68rem",
                          py: 0.6,
                          bgcolor: "rgba(245,247,255,0.9)",
                        }}
                      />
                      <TableCell
                        sx={{
                          fontSize: "0.68rem",
                          py: 0.6,
                          bgcolor: "rgba(245,247,255,0.9)",
                        }}
                      >
                        Name
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.68rem",
                          py: 0.6,
                          bgcolor: "rgba(245,247,255,0.9)",
                        }}
                      >
                        Content
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.68rem",
                          py: 0.6,
                          bgcolor: "rgba(245,247,255,0.9)",
                        }}
                      >
                        Media
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTemplates.map((tpl) => {
                      const bodyText = getBodyText(tpl);
                      const headerImg = getHeaderImageUrl(tpl);
                      return (
                        <TableRow
                          key={tpl.template_id}
                          hover
                          onClick={() => handleTemplateRowClick(tpl)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell sx={{ py: 0.4, pl: 1 }}>
                            <Radio
                              size="small"
                              checked={selectedTemplateId === tpl.template_id}
                              sx={{ p: 0 }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.73rem", py: 0.5 }}>
                            {tpl.name}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "0.7rem",
                              py: 0.5,
                              maxWidth: 120,
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              color: "text.secondary",
                            }}
                          >
                            {bodyText}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.7rem", py: 0.5 }}>
                            {headerImg ? "Image" : "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!isTplLoading && filteredTemplates.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography
                            sx={{
                              fontSize: "0.73rem",
                              color: "text.secondary",
                              textAlign: "center",
                              py: 1,
                            }}
                          >
                            No templates found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Paper>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: "rgba(255,255,255,0.8)",
          borderTop: "1px solid rgba(0,0,0,0.08)",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.4, px: 2 }}
          onClick={() =>
            navigate(isEditMode ? `/workflow/edit/${id}` : `/workflow/${id}`)
          }
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.4, px: 2 }}
          disabled={
            !campaignName ||
            !phoneNumber ||
            !selectedTemplateId
          }
          onClick={handleSaveToWorkflow}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default WhatsappConfig;